import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { getAllowedPaths, canAccess, ROUTES } from '@/lib/permissions';
import {
  Building2,
  LayoutDashboard,
  Package,
  Wrench,
  BarChart3,
  AlertCircle,
  Shield,
  BrainCircuit,
  LogOut,
  Globe,
  Moon,
  Sun,
} from 'lucide-react';

const allNavItems = [
  { path: ROUTES.DASHBOARD, icon: LayoutDashboard, labelKey: 'dashboard' },
  { path: ROUTES.ASSETS, icon: Package, labelKey: 'assets' },
  { path: ROUTES.WORK_ORDERS, icon: Wrench, labelKey: 'workOrders' },
  { path: ROUTES.REPAIR_VS_REPLACE, icon: BarChart3, labelKey: 'repairVsReplace' },
  { path: ROUTES.FAULT_REPORT, icon: AlertCircle, labelKey: 'faultReport' },
  { path: ROUTES.BOZP, icon: Shield, labelKey: 'bozp' },
  { path: ROUTES.PREDICTIVE, icon: BrainCircuit, labelKey: 'predictiveMaintenance' },
];

export const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    return user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const pathname = location.pathname;
  const allowedPaths = getAllowedPaths(user?.role);
  const navItems = allNavItems.filter((item) => allowedPaths.includes(item.path));
  const knownPaths = Object.values(ROUTES);
  const currentNav = navItems.find((item) => item.path === pathname);
  const isUnknownPath = !knownPaths.includes(pathname);
  const currentLabel = currentNav ? t(currentNav.labelKey) : (isUnknownPath ? t('pageNotFound') : null);
  const showCurrentPage = (currentLabel && pathname !== '/dashboard' && pathname !== '/') || isUnknownPath;

  const isKnownPath = knownPaths.includes(pathname);
  if (isKnownPath && !canAccess(user?.role, pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-background flex">
      {/* Desktop: levý sidebar s logem a navigací */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:left-0 lg:top-0 lg:h-screen lg:w-64 lg:border-r lg:border-border lg:bg-card lg:shadow-sm lg:z-40">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">FM System</h1>
              <p className="text-xs text-muted-foreground">Facility Management</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span>{t(item.labelKey)}</span>
                </div>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-sm">{getUserInitials()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{t(user?.role)}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} aria-label={t('logout')}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Pravá část: header + main + footer (na desktopu s odsazením za sidebar) */}
      <div className="flex-1 flex flex-col min-h-screen lg:pl-64">
        {/* Horní pruh: na mobilu logo + akce, na desktopu jen breadcrumb + akce */}
        <header className="sticky top-0 z-50 w-full border-b border-border bg-card shadow-sm">
          <div className="flex h-14 lg:h-12 items-center gap-3 px-4 lg:gap-4 lg:px-6">
            {/* Logo jen na mobilu */}
            <div className="flex items-center gap-3 shrink-0 lg:hidden">
              <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-base font-bold text-foreground">FM System</h1>
              </div>
            </div>
            {/* Breadcrumb – vždy */}
            <div className="flex-1 min-w-0">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/dashboard">{t('dashboard')}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {showCurrentPage && (
                    <>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbPage>{currentLabel}</BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="gap-2"
                aria-label={theme === 'dark' ? 'Světlý režim' : 'Tmavý režim'}
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'CS' : 'EN'}</span>
              </Button>
              <Button variant="ghost" size="sm" className="lg:hidden" onClick={handleLogout} aria-label={t('logout')}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main – bez breadcrumb (už v headeru) */}
        <main className="flex-1 container mx-auto px-4 lg:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
          <ErrorBoundary onGoHome={() => navigate('/dashboard')}>
            <Outlet />
          </ErrorBoundary>
        </main>

        {/* Mobilní: navigace fixně dole (bottom bar) */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card shadow-lg">
          <div className="flex items-center justify-around h-16 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-2 rounded-lg transition-colors ${
                    isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon className="w-6 h-6 shrink-0" />
                  <span className="text-[10px] truncate max-w-full">{t(item.labelKey)}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <footer className="border-t border-border bg-card mt-12 mb-20 lg:mb-0">
          <div className="container mx-auto px-4 lg:px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                <p className="text-sm text-muted-foreground">{t('footerCopyright')}</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
